import { HighchartsChart } from '@sede-enterprise/shell-ds-react-framework';
import { cxTheme } from 'components/storybook-migration/theme-provider/system-design/theme';
import Highcharts, { TooltipFormatterContextObject } from 'highcharts';
import { AvailabilityChartType } from 'pages/Availability/common/AvailabilityChart/types';
import { powerColors } from 'pages/Availability/common/AvailabilityChart/utils/chartDataUtils';
import {
  getDateTickInterval,
  tooltipFormatter,
  xAxisLabelsFormatter,
  yAxisLabelsFormatter,
} from 'pages/Availability/common/AvailabilityChart/utils/chartDisplayUtils';
import { getMaxSiteLimit } from 'pages/Availability/common/utils';
import { ChartSeries } from 'pages/Availability/types';
import { useStore } from 'store';
import { Timezone } from 'types/timezone';

interface AvailabilityChartProps {
  type: AvailabilityChartType;
  data: ChartSeries[] | null;
  timezone: Timezone;
}

const AXIS_EDGE_SPACING = 2;

export const AvailabilityChart: React.FC<AvailabilityChartProps> = ({
  type,
  data,
  timezone,
}) => {
  const isPower = type === 'power';
  const appTheme = useStore((state) => state.appTheme);

  const siteData = useStore((state) => state.selectedSiteData);

  const latestSiteLimit = getMaxSiteLimit(siteData?.siteLimits ?? []);
  const maxPower = latestSiteLimit?.maxExportPower ?? 0;
  const minPower = latestSiteLimit?.maxImportPower ?? 0;
  const maxEnergy = latestSiteLimit?.energyCapacity ?? 0;

  const tickInterval = getDateTickInterval(data);

  return (
    <HighchartsChart
      key={`${appTheme}-${type}-${data ? 'data' : 'empty'}`}
      highcharts={Highcharts}
      options={{
        plotOptions: {
          series: {
            animation: false,
            step: 'left',
          },
        },
        chart: {
          type: 'area',
          spacingTop: AXIS_EDGE_SPACING,
          spacingBottom: AXIS_EDGE_SPACING,
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function (this: TooltipFormatterContextObject) {
            return tooltipFormatter.call(this, isPower);
          },
        },
        legend: {
          itemStyle: {
            color:
              appTheme === 'dark'
                ? cxTheme.colors.neutral.offWhite
                : cxTheme.colors.neutral.darken60,
          },
        },
        yAxis: {
          min: isPower ? -minPower : 0,
          max: isPower ? maxPower : maxEnergy,
          title: {
            useHTML: true,
            text: isPower ? '<h2>Power</h2>' : '<h2>Energy</h2>',
            style: {
              color:
                appTheme === 'dark'
                  ? cxTheme.colors.neutral.offWhite
                  : cxTheme.colors.neutral.darken60,
            },
          },
          labels: {
            formatter: yAxisLabelsFormatter(isPower),
            style: {
              color:
                appTheme === 'dark'
                  ? cxTheme.colors.neutral.offWhite
                  : cxTheme.colors.neutral.darken60,
            },
          },
          gridLineColor:
            appTheme === 'dark'
              ? cxTheme.colors.neutral.darken60
              : cxTheme.colors.neutral.base,
          plotBands: isPower
            ? [
                {
                  from: 0,
                  to: Infinity,
                  color: 'transparent',
                  label: {
                    useHTML: true,
                    text: '<h4>Export</h4>',
                    align: 'left',
                    verticalAlign: 'middle',
                    y: 15,
                    rotation: -90,
                    style: { fontWeight: '200', color: powerColors.lineColor },
                  },
                },
                {
                  from: -Infinity,
                  to: 0,
                  color: 'transparent',
                  label: {
                    useHTML: true,
                    text: '<h4>Import</h4>',
                    align: 'left',
                    verticalAlign: 'middle',
                    y: 15,
                    rotation: -90,
                    style: { fontWeight: '200', color: powerColors.lineColor },
                  },
                },
              ]
            : [],
          plotLines: [
            isPower
              ? {
                  value: 0,
                  width: 1,
                  color: cxTheme.colors.neutral.darken40,
                  zIndex: 3,
                  dashStyle: 'dash',
                }
              : {},
          ],
        },
        xAxis: {
          lineColor: cxTheme.colors.neutral.darken40,
          tickColor: cxTheme.colors.neutral.darken40,
          type: 'datetime',
          tickInterval: tickInterval,
          offset: isPower ? 20 : 0,
          crosshair: {
            width: 1,
            dashStyle: 'Dash',
            color: '#000',
            snap: true,
          },
          labels: {
            formatter: xAxisLabelsFormatter,
            style: {
              color:
                appTheme === 'dark'
                  ? cxTheme.colors.neutral.offWhite
                  : cxTheme.colors.neutral.darken60,
            },
          },
        },
        series: data,
        time: {
          timezone: timezone,
        },
      }}
    />
  );
};
